import { useState, useEffect, useCallback } from "react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Loader2, Save, Plus, GripVertical, ArrowUp, ArrowDown, X } from "lucide-react";
import { toast } from "sonner";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FIELD_TYPES = [
  { value: "text", label: "Texto" }, { value: "number", label: "Número" },
  { value: "select", label: "Lista (opciones)" }, { value: "textarea", label: "Párrafo" },
  { value: "date", label: "Fecha" }, { value: "phone", label: "Teléfono" }, { value: "email", label: "Email" },
];

export default function LeadFormConfigDialog({ pipelineId, getAuthHeaders, onClose }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [available, setAvailable] = useState([]);
  const [ramos, setRamos] = useState([]);
  const [cfg, setCfg] = useState({ show_email: true, email_required: false, show_mensaje: true, ramo_required: true, fields: [] });
  const [newField, setNewField] = useState({ label: "", field_type: "text", options: "" });
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/lead-form-config`, getAuthHeaders());
      const d = await r.json();
      setAvailable(d.available_fields || []);
      setRamos(d.ramos || []);
      setCfg({
        show_email: d.config?.show_email ?? true,
        email_required: d.config?.email_required ?? false,
        show_mensaje: d.config?.show_mensaje ?? true,
        ramo_required: d.config?.ramo_required ?? true,
        fields: (d.config?.fields || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0)),
      });
    } catch { toast.error("Error al cargar la configuración"); }
    finally { setLoading(false); }
  }, [pipelineId, getAuthHeaders]);

  useEffect(() => { load(); }, [load]);

  const isIncluded = (name) => cfg.fields.some((f) => f.name === name);
  const labelOf = (name) => available.find((a) => a.name === name)?.label || name;

  const toggleInclude = (name, on) => {
    setCfg((c) => on
      ? { ...c, fields: [...c.fields, { name, required: false, ramos: [] }] }
      : { ...c, fields: c.fields.filter((f) => f.name !== name) });
  };
  const updField = (name, patch) => setCfg((c) => ({ ...c, fields: c.fields.map((f) => f.name === name ? { ...f, ...patch } : f) }));
  const toggleRamo = (name, ramo) => setCfg((c) => ({
    ...c, fields: c.fields.map((f) => {
      if (f.name !== name) return f;
      const set = new Set(f.ramos || []);
      if (set.has(ramo)) set.delete(ramo); else set.add(ramo);
      return { ...f, ramos: [...set] };
    }),
  }));
  const move = (idx, dir) => setCfg((c) => {
    const arr = [...c.fields]; const j = idx + dir;
    if (j < 0 || j >= arr.length) return c;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...c, fields: arr };
  });

  const createField = async () => {
    if (!newField.label.trim()) { toast.error("Indicá el nombre del campo"); return; }
    setCreating(true);
    try {
      const name = newField.label.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
      const body = { name, label: newField.label.trim(), field_type: newField.field_type,
        options: newField.field_type === "select" ? newField.options.split(",").map((o) => o.trim()).filter(Boolean) : [] };
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/fields`, {
        method: "POST", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error();
      toast.success("Campo creado y agregado al pipeline");
      setNewField({ label: "", field_type: "text", options: "" });
      await load();
      toggleInclude(name, true);
    } catch { toast.error("Error al crear el campo"); }
    finally { setCreating(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg, fields: cfg.fields.map((f, i) => ({ name: f.name, required: !!f.required, order: i, ramos: f.ramos || [] })) };
      const r = await fetch(`${API_URL}/api/pipelines/${pipelineId}/lead-form-config`, {
        method: "PUT", headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" }, body: JSON.stringify(payload) });
      if (!r.ok) throw new Error();
      toast.success("Formulario configurado"); onClose();
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="leadform-config-dialog">
        <DialogHeader><DialogTitle>Configurar campos del formulario de leads</DialogTitle></DialogHeader>
        {loading ? (
          <div className="text-center p-10"><Loader2 className="w-6 h-6 mx-auto animate-spin text-slate-400" /></div>
        ) : (
          <div className="space-y-5">
            {/* Campos base */}
            <div className="border rounded-lg p-3 space-y-2 bg-slate-50">
              <Label className="text-xs font-bold uppercase tracking-wide text-slate-600">Campos base</Label>
              <Row label="Nombre y teléfono (siempre obligatorios)"><span className="text-xs text-slate-400">Fijos</span></Row>
              <Row label="Mostrar Email"><Switch checked={cfg.show_email} onCheckedChange={(v) => setCfg({ ...cfg, show_email: v })} data-testid="cfg-show-email" /></Row>
              {cfg.show_email && <Row label="Email obligatorio" sub><Switch checked={cfg.email_required} onCheckedChange={(v) => setCfg({ ...cfg, email_required: v })} /></Row>}
              <Row label="Mostrar Mensaje"><Switch checked={cfg.show_mensaje} onCheckedChange={(v) => setCfg({ ...cfg, show_mensaje: v })} /></Row>
              <Row label="Ramo de interés obligatorio"><Switch checked={cfg.ramo_required} onCheckedChange={(v) => setCfg({ ...cfg, ramo_required: v })} /></Row>
            </div>

            {/* Campos del formulario */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wide text-[#193966]">Campos adicionales del formulario</Label>
              <p className="text-[11px] text-slate-500 mb-2">Activá los campos que querés pedirle al lead. Lo que complete quedará guardado en la oportunidad.</p>
              <div className="space-y-2">
                {available.length === 0 && <p className="text-xs text-slate-400">No hay campos disponibles. Creá uno abajo.</p>}
                {/* selected first, in order */}
                {cfg.fields.map((f, idx) => (
                  <div key={f.name} className="border rounded-lg p-3 bg-white" data-testid={`cfg-field-${f.name}`}>
                    <div className="flex items-center gap-2">
                      <GripVertical className="w-4 h-4 text-slate-300" />
                      <span className="font-semibold text-sm flex-1">{labelOf(f.name)}</span>
                      <button onClick={() => move(idx, -1)} className="text-slate-400 hover:text-slate-700"><ArrowUp className="w-4 h-4" /></button>
                      <button onClick={() => move(idx, 1)} className="text-slate-400 hover:text-slate-700"><ArrowDown className="w-4 h-4" /></button>
                      <button onClick={() => toggleInclude(f.name, false)} className="text-rose-400 hover:text-rose-600"><X className="w-4 h-4" /></button>
                    </div>
                    <div className="flex items-center gap-4 mt-2 pl-6">
                      <label className="flex items-center gap-1.5 text-xs"><Switch checked={!!f.required} onCheckedChange={(v) => updField(f.name, { required: v })} /> Obligatorio</label>
                    </div>
                    <div className="pl-6 mt-2">
                      <p className="text-[10px] text-slate-500 mb-1">Mostrar para los ramos: <b>{(f.ramos || []).length ? f.ramos.join(", ") : "Todos"}</b></p>
                      <div className="flex flex-wrap gap-1.5">
                        {ramos.map((r) => (
                          <button key={r} type="button" onClick={() => toggleRamo(f.name, r)}
                            className={`text-[11px] px-2 py-0.5 rounded-full border transition-colors ${(f.ramos || []).includes(r) ? "bg-[#193966] text-white border-[#193966]" : "bg-white text-slate-600 border-slate-300"}`}>
                            {r}
                          </button>
                        ))}
                        {ramos.length === 0 && <span className="text-[10px] text-slate-400">El campo "Ramo" (producto) no tiene opciones configuradas.</span>}
                      </div>
                    </div>
                  </div>
                ))}
                {/* available not selected */}
                {available.filter((a) => !isIncluded(a.name)).length > 0 && (
                  <div className="border border-dashed rounded-lg p-2">
                    <p className="text-[10px] text-slate-400 mb-1">Disponibles (clic para agregar):</p>
                    <div className="flex flex-wrap gap-1.5">
                      {available.filter((a) => !isIncluded(a.name)).map((a) => (
                        <button key={a.name} type="button" onClick={() => toggleInclude(a.name, true)}
                          className="text-[11px] px-2 py-1 rounded border border-slate-300 bg-white hover:bg-slate-50 flex items-center gap-1" data-testid={`cfg-add-${a.name}`}>
                          <Plus className="w-3 h-3" /> {a.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Crear campo nuevo */}
            <div className="border rounded-lg p-3 bg-green-50/40">
              <Label className="text-xs font-bold uppercase tracking-wide text-green-700">Crear campo nuevo</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                <Input value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} placeholder="Ej: Valor asegurado" className="h-9 col-span-2" data-testid="cfg-newfield-label" />
                <select value={newField.field_type} onChange={(e) => setNewField({ ...newField, field_type: e.target.value })} className="h-9 border border-slate-300 rounded px-2 text-sm bg-white">
                  {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              {newField.field_type === "select" && (
                <Input value={newField.options} onChange={(e) => setNewField({ ...newField, options: e.target.value })} placeholder="Opciones separadas por coma" className="h-9 mt-2" />
              )}
              <Button size="sm" variant="outline" className="h-8 mt-2" onClick={createField} disabled={creating} data-testid="cfg-newfield-create">
                {creating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Plus className="w-3.5 h-3.5 mr-1" />} Crear y agregar
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={save} disabled={saving || loading} className="bg-[#80BC00] hover:bg-[#6ea700] text-white" data-testid="leadform-config-save">
            {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />} Guardar configuración
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, sub, children }) => (
  <div className={`flex items-center justify-between ${sub ? "pl-4" : ""}`}>
    <span className={`text-sm ${sub ? "text-slate-500" : "text-slate-700"}`}>{label}</span>
    {children}
  </div>
);
